<template>
  <div v-if="allowCreateNew">
    <OkrElementCreator
      :disabled="isLoading"
      :follow-cursor="false"
      :objective-levels="levelsForCreateElement"
      :offset="[0, 0]"
      :select-options-list-test-id="HOMEPAGE_OKR_ELEMENT_CREATOR_TEST_ID"
      droplist-position="bottom-start"
      type="default-next"
      @on-create-okr-element="onCreateObjectiveClick($event, workspaceIdForCreateElement)"
      @droplist-hidden="cleanUpCreation"
    >
      <template #trigger="{ onTriggerClick }">
        <AppSelect
          v-if="shouldChooseWorkspace && !workspaceIdForCreateElement"
          :disabled="isLoading"
          :item-label="WORKSPACE_ENTITY_KEYS.NAME"
          :item-value="WORKSPACE_ENTITY_KEYS.ID"
          :model-value="workspaceIdForCreateElement"
          :offset="[0, 0]"
          :options="selectedWorkspacesWithCreatePermissions"
          data-export-ignore
          dropdown-width="200px"
          @update:model-value="onSelectWorkspace($event, onTriggerClick)"
        >
          <template #option-label="{ option }">
            <WorkspaceSelectOption v-if="option" :workspace="option" hide-lock-icon show-tooltip />
          </template>

          <template #button>
            <AppButton
              :disable="isLoading"
              data-export-ignore
              height="24"
              icon="plus-next"
              type="primary-next"
              width="24"
            >
              {{ $t('action.create') }}
            </AppButton>
          </template>
        </AppSelect>

        <AppButton
          v-else
          :disable="isLoading"
          data-export-ignore
          height="24"
          icon="plus-next"
          type="primary-next"
          width="24"
          @click="onCreateClick(onTriggerClick)"
        >
          {{ $t('action.create') }}
        </AppButton>
      </template>
    </OkrElementCreator>
  </div>

  <portal to="modal-windows">
    <ObjectiveModal
      v-if="showObjectiveModal"
      ref="objectiveModal"
      source="button"
      @close="onObjectiveModalClose"
      @closed="showObjectiveModal = false"
      @element-created="showElementCreatedNotification"
    />

    <CheckinModal
      v-if="isShowCheckinModal"
      v-model="isCheckinModalOpen"
      :entry-point="checkinModalLaunchData.entryPoint"
      :okr-element="checkinModalLaunchData.okrElement"
      :preset-backdate="checkinModalLaunchData.presetBackdate"
      :tracking-source="EVENT_SOURCES.HOMEPAGE"
      @saved="$emit('update-okr-elements')"
      @after-leave="onCheckinModalClosed"
    />
  </portal>
</template>

<script>
import { isEmpty, isFunction } from 'lodash'
import { defineComponent, ref } from 'vue'
import { useRoute } from 'vue-router'
import { mapState } from 'vuex'

import HomepageGroupsApiHandler from '@/api/homepage-groups'
import HomepageUsersApiHandler from '@/api/homepage-users'
import IntervalsInfoApiHandler from '@/api/intervals-info'
import { ROUTE_NAMES } from '@/routes/route-helpers'
import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { useCheckinModal } from '@/utils/check-ins/check-ins'
import { useElementTypes } from '@/utils/element-types'
import {
  ASSIGNEE_ENTITY_KEYS,
  OKR_ELEMENT_ENTITY_KEYS,
  WORKSPACE_ENTITY_KEYS
} from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { GLOBAL_GROUP_KEYS } from '@/utils/global-groups'
import { getSuitableInterval } from '@/utils/interval'
import { isKR, OBJECTIVE_TYPES } from '@/utils/objective-types'
import {
  currentUserCanCreateObjective,
  OKR_FORM_VIEWS,
  OKR_TYPE_TO_FORM_VIEW,
  okrElementToLinkIssuePayload,
  okrElementToLinkWorkItemPayload,
  ROLE_IDS
} from '@/utils/objectives'
import { useObjectiveModalNotifications } from '@/utils/okr-elements/notifications'
import { getEditValueFromQuery } from '@/utils/okr-elements-forms-helpers'
import { useLoadingState } from '@/utils/use-loading-state'

import CheckinModal from '@/components/objectives/element-check-in/CheckinModal'
import ObjectiveModal from '@/components/objectives/ObjectiveModal'
import OkrElementCreator from '@/components/objectives/OkrElementCreator'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import { HOMEPAGE_OKR_ELEMENT_CREATOR_TEST_ID } from '@/components/ui/AppSelect/jest-helpers'
import WorkspaceSelectOption from '@/components/ui/WorkspaceSelectOption/WorkspaceSelectOption'

export default defineComponent({
  name: 'PersonalStatisticsOkrElementsModals',
  components: {
    WorkspaceSelectOption,
    AppSelect,
    CheckinModal,
    AppButton,
    OkrElementCreator,
    ObjectiveModal
  },

  inheritAttrs: false,

  props: {
    selectedUserOrGroup: {
      type: [String, Number],
      required: true
    },

    userSelected: {
      type: Boolean
    },

    selectedWorkspaceIds: {
      type: Array,
      required: true
    },

    skipOpenElementModalFromQuery: {
      type: Boolean,
      default: false
    }
  },

  emits: {
    'update-okr-elements': null
  },

  setup() {
    const { getElementTypeIdByLevelId } = useElementTypes()
    const {
      isCheckinModalOpen,
      handleOpenCheckinModal,
      checkinModalLaunchData,
      isShowCheckinModal,
      onCheckinModalClosed
    } = useCheckinModal()

    const workspaceIdForCreateElement = ref(null)

    const { isLoading, toggleLoading } = useLoadingState()

    const cleanUpCreation = () => {
      workspaceIdForCreateElement.value = null
    }

    const route = useRoute()
    const isModalOpen = ref(false)

    const { showModalCloseNotifications, showElementCreatedNotification } =
      useObjectiveModalNotifications({
        isModalOpen,
        preferredPath: () => route.path
      })

    return {
      getElementTypeIdByLevelId,
      isCheckinModalOpen,
      handleOpenCheckinModal,
      checkinModalLaunchData,
      isShowCheckinModal,
      onCheckinModalClosed,
      workspaceIdForCreateElement,
      isLoading,
      toggleLoading,
      cleanUpCreation,
      showObjectiveModal: isModalOpen,
      showModalCloseNotifications,
      showElementCreatedNotification
    }
  },

  computed: {
    ...mapState('system', {
      userData: state => state.userData
    }),

    WORKSPACE_ENTITY_KEYS: () => WORKSPACE_ENTITY_KEYS,

    EVENT_SOURCES: () => EVENT_SOURCES,

    HOMEPAGE_OKR_ELEMENT_CREATOR_TEST_ID: () => HOMEPAGE_OKR_ELEMENT_CREATOR_TEST_ID,

    selectedWorkspacesWithCreatePermissions() {
      if (!this.$store) return []

      if (isEmpty(this.selectedWorkspaceIds)) return []

      const { isPluginAdmin } = this.$store.state.pluginOptions

      return this.selectedWorkspaceIds.reduce((acc, val) => {
        const workspace = this.$store.getters['workspaces/getWorkspaceById'](val)
        if (workspace && currentUserCanCreateObjective(workspace, isPluginAdmin)) {
          acc.push(workspace)
        }
        return acc
      }, [])
    },

    shouldChooseWorkspace() {
      return this.selectedWorkspacesWithCreatePermissions.length > 1
    },

    levelsForCreateElement() {
      if (!this.workspaceIdForCreateElement) return []

      return this.$store.getters['objectives/getLevelsByWorkspaceId'](
        this.workspaceIdForCreateElement
      )
    },

    allowCreateNew() {
      if (!this.$store) return false

      return !isEmpty(this.selectedWorkspacesWithCreatePermissions)
    }
  },

  mounted() {
    if (this.$route && !this.skipOpenElementModalFromQuery) {
      const { query } = this.$route

      const editValue = getEditValueFromQuery({ query })

      if (editValue) {
        this.onEditElement(editValue)
      }
    }
  },

  methods: {
    async getIntervalsList(workspaceId) {
      const api = new IntervalsInfoApiHandler()

      try {
        const { intervals } = await api.getIntervalsInfo({ workspaceId })

        return intervals
      } catch (error) {
        handleError({ error })

        return []
      }
    },

    async onEditElement(elementData) {
      const { typeId, id, children } = elementData
      this.showObjectiveModal = true
      await this.$nextTick()
      this.$refs.objectiveModal.openForm(OKR_TYPE_TO_FORM_VIEW[typeId], {
        typeId,
        id
      })
      if (children) {
        const { typeId, id } = children
        this.$refs.objectiveModal.openChildForm(OKR_TYPE_TO_FORM_VIEW[typeId], {
          typeId,
          id
        })
      }
    },

    async isSelectedEntityInWorkspace(selectedUserOrGroup, workspaceId) {
      try {
        if (this.userSelected) {
          const api = new HomepageUsersApiHandler()
          const user = await api.getUserById(selectedUserOrGroup)

          return (user?.[ASSIGNEE_ENTITY_KEYS.WORKSPACE_IDS] || []).includes(workspaceId)
        }

        const api = new HomepageGroupsApiHandler()

        const group = await api.getGroupById(selectedUserOrGroup)

        return (group?.[GLOBAL_GROUP_KEYS.WORKSPACE_IDS] || []).includes(workspaceId)
      } catch (error) {
        handleError({ error })

        return false
      }
    },

    async onCreateObjectiveClick(levelId, selectedWorkspaceId) {
      const typeId = this.getElementTypeIdByLevelId({ levelId })

      const { userSelected, selectedUserOrGroup } = this

      this.toggleLoading(true)
      const [intervalsForSelectedWorkspace, isSelectedInWorkspace] = await Promise.all([
        this.getIntervalsList(selectedWorkspaceId),
        this.isSelectedEntityInWorkspace(selectedUserOrGroup, selectedWorkspaceId)
      ]).finally(() => this.toggleLoading(false))

      const intervalId = getSuitableInterval(intervalsForSelectedWorkspace, [], true)

      this.showObjectiveModal = true

      const openFormPayload = {
        levelId,
        typeId,
        intervalId: intervalId ?? null,
        workspaceId: selectedWorkspaceId
      }

      if (userSelected) {
        if (isSelectedInWorkspace) {
          openFormPayload.users = [
            {
              elementRoleId: ROLE_IDS.OWNER,
              accountId: selectedUserOrGroup
            }
          ]
        }
      } else {
        if (isSelectedInWorkspace) {
          openFormPayload.groups = [
            {
              id: selectedUserOrGroup
            }
          ]
        }

        openFormPayload.users = [
          {
            elementRoleId: ROLE_IDS.OWNER,
            accountId: this.userData.userAccountId
          }
        ]
      }

      const view = isKR({
        typeId
      })
        ? OKR_FORM_VIEWS.KR
        : OKR_FORM_VIEWS.OBJECTIVE

      await this.$nextTick()
      this.$refs.objectiveModal.openForm(view, openFormPayload, 'grid')

      this.cleanUpCreation()
    },

    onObjectiveModalClose(eventData) {
      this.showModalCloseNotifications(eventData, {
        onOpen: createdElement => {
          this.editObjective(
            OKR_TYPE_TO_FORM_VIEW[createdElement[OKR_ELEMENT_ENTITY_KEYS.TYPE_ID]],
            createdElement
          )
        }
      })

      const parameters = {
        keepObjectivesSize: true
      }

      const createdObject = eventData.createdObjective || eventData.createdKR

      const handleOkrElement = element => {
        parameters.blinkObjectiveIds = [
          {
            uniqueId: element.uniqueId,
            // onFirstLevel: true
            onFirstLevel: false
          }
        ]
      }

      if (createdObject) {
        handleOkrElement(createdObject)
      } else if (eventData.createdTasks) {
        eventData.createdTasks.forEach(issue => handleOkrElement(issue))
      } else if (eventData.createdWorkItems) {
        eventData.createdWorkItems.forEach(workItem => handleOkrElement(workItem))
      }

      this.$emit('update-okr-elements', parameters)
    },

    async onSelectWorkspace(workspaceId, cb) {
      this.workspaceIdForCreateElement = workspaceId

      await this.$nextTick()
      if (cb && isFunction(cb)) {
        cb()
      }
    },

    onCreateClick(cb) {
      if (!cb || !isFunction(cb)) return

      if (this.workspaceIdForCreateElement) {
        cb()
        return
      }

      const [workspace] = this.selectedWorkspacesWithCreatePermissions
      this.onSelectWorkspace(workspace.id, cb)
    },

    /** @public */
    async createKR(view, parentOkrElement) {
      this.showObjectiveModal = true
      await this.$nextTick()
      this.$refs.objectiveModal.openForm(
        view,
        {
          typeId: OBJECTIVE_TYPES.KR,
          intervalId: parentOkrElement.intervalId,
          parentId: parentOkrElement.id,
          workspaceId: parentOkrElement.workspaceId,
          automaticElementStartDate: parentOkrElement.automaticElementStartDate,
          automaticDueDate: parentOkrElement.automaticDueDate,
          intervalStartDate: parentOkrElement.intervalStartDate,
          intervalEndDate: parentOkrElement.intervalEndDate
          // users: data.users
        },
        ROUTE_NAMES.HOME_PAGE.toLowerCase()
      )
    },

    /** @public */
    async editObjective(view, data, payload) {
      this.showObjectiveModal = true
      await this.$nextTick()
      this.$refs.objectiveModal.openForm(
        view,
        data,
        ROUTE_NAMES.HOME_PAGE.toLowerCase(),
        true,
        payload
      )
    },

    /** @public */
    async linkJiraIssue(view, data) {
      this.showObjectiveModal = true
      await this.$nextTick()
      const payload = okrElementToLinkIssuePayload(data)
      this.$refs.objectiveModal.openForm(view, payload, ROUTE_NAMES.HOME_PAGE.toLowerCase())
    },

    /** @public */
    async linkWorkItem(view, data) {
      this.showObjectiveModal = true
      await this.$nextTick()
      const payload = okrElementToLinkWorkItemPayload(data)
      this.$refs.objectiveModal.openForm(view, payload, ROUTE_NAMES.HOME_PAGE.toLowerCase())
    },

    /** @public */
    openCheckinModal(payload) {
      this.handleOpenCheckinModal(payload)
    }
  }
})
</script>

<style lang="scss" scoped></style>
